import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  EXHIBITION_PRESET_IDS,
  ExhibitionPresetId,
  type ExhibitionPresetId as ExhibitionPresetIdType,
} from '@paladin-monorepo/common';

import { KioskLayout } from '@/layouts/KioskLayout';
import { ExhibitionAccountTypeDialog } from '@/pages/monomo-ai/ExhibitionAccountTypeDialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { exhibitionApi } from '@/lib/api/exhibition';
import { useAuth } from '@/lib/auth';
import {
  clearExhibitionTypeConfirmed,
  isExhibitionTypeConfirmed,
  needsExhibitionTypeDialog,
} from '@/lib/exhibition';
import { cn } from '@/lib/utils';

export function ExhibitionStartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth({ tmp: 'ExhibitionStartPage' });
  const [selected, setSelected] = useState<ExhibitionPresetIdType | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [typeDialogConfirmed, setTypeDialogConfirmed] = useState(
    () => user?.account?.id != null && isExhibitionTypeConfirmed(user.account.id),
  );
  const preselectAppliedRef = useRef(false);

  const typeDialogPending =
    !typeDialogConfirmed &&
    needsExhibitionTypeDialog(user, searchParams.get('exhibition') === '1');

  const { data: suggestion, isLoading: isSuggestionLoading } = useQuery({
    queryKey: ['/exhibition/preset-suggestion'],
    queryFn: () => exhibitionApi.getPresetSuggestion(),
  });

  useEffect(() => {
    if (preselectAppliedRef.current || isSuggestionLoading) return;
    preselectAppliedRef.current = true;
    setSelected(suggestion?.suggestedPresetId ?? ExhibitionPresetId.Other);
  }, [isSuggestionLoading, suggestion?.suggestedPresetId]);

  const current = selected ?? ExhibitionPresetId.Other;

  const start = async () => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      const { sessionId, attachmentIds } = await exhibitionApi.startSession({ presetId: current });
      const initialMessage =
        current === ExhibitionPresetId.Empty
          ? undefined
          : t(`exhibition.presets.${current}.prompt`);
      navigate(`/llm-chat/${sessionId}`, {
        state: {
          ...(initialMessage ? { initialMessage } : {}),
          ...(attachmentIds.length > 0 ? { initialAttachmentIds: attachmentIds } : {}),
        },
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        if (user?.account?.id) {
          clearExhibitionTypeConfirmed(user.account.id);
          setTypeDialogConfirmed(false);
        }
        toast.error(t('exhibition.start.errors.agentUnavailable'));
      } else {
        toast.error(t('exhibition.start.errors.default'));
      }
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <KioskLayout contentClassName="items-center justify-center p-4">
      <ExhibitionAccountTypeDialog
        key={typeDialogConfirmed ? 'confirmed' : 'pending'}
        onConfirmed={() => setTypeDialogConfirmed(true)}
      />

      <Card className="w-full max-w-2xl px-4 py-8">
        <CardHeader>
          <CardTitle className="text-xl">{t('exhibition.start.title')}</CardTitle>
          <CardDescription>{t('exhibition.start.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {isSuggestionLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {EXHIBITION_PRESET_IDS.map((presetId) => (
                <button
                  key={presetId}
                  type="button"
                  disabled={isStarting}
                  onClick={() => setSelected(presetId)}
                  className={cn(
                    'rounded-md border p-3 text-left transition-colors',
                    current === presetId
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted',
                  )}
                >
                  <span className="font-medium">{t(`exhibition.presets.${presetId}.label`)}</span>
                  <p className="text-sm text-muted-foreground">
                    {t(`exhibition.presets.${presetId}.description`)}
                  </p>
                </button>
              ))}
            </div>
          )}

          <Button
            className="py-5"
            type="button"
            onClick={() => void start()}
            disabled={isStarting || isSuggestionLoading || typeDialogPending}
          >
            {isStarting ? t('exhibition.start.starting') : t('exhibition.start.submit')}
          </Button>
        </CardContent>
      </Card>
    </KioskLayout>
  );
}

export default ExhibitionStartPage;
